import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { markOnboardingAsViewed } from '../../actions/onbording';
import { Portal } from '../../components/Notificator/Portal';
import { OnbordingBanner } from '../../components/Onbording/OnbordingBanner';
import { OnbordingTooltip } from '../../components/Onbording/OnbordingTooltip';
import { EScrollingBlocks } from '../../constants/scrollingBlocks';
import { VIEWED_TIMEOUT } from '../../constants/viewedBlock';
import { selectIsDesktop } from '../../selectors/page';
import { useScrollToBlock } from '../Scrolling';
import { trackOnbordingCloseClick, trackOnbordingItemClick, trackViewOnbordingBanner } from './tracking';
import { useGetOnbordingItems } from './utils';
import { TThunkDispatch } from '../../types/redux';

const ONBORDING_HEIGHT = 200;

export const OnbordingContainer: React.FC = () => {
  const onbordingItems = useGetOnbordingItems();
  const isDesktop = useSelector(selectIsDesktop);
  const dispatch = useDispatch<TThunkDispatch>();
  const scrollToBlock = useScrollToBlock();
  const [tooltipOpen, setTooltipOpen] = React.useState(false);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [bannerVisible, setBannerVisible] = React.useState(true);
  const [stopOnbording, setStopOnbording] = React.useState(false);
  const [coords, setCoords] = React.useState({ top: 0, left: 0 });

  const [content, setContent] = React.useState({
    title: '',
    description: '',
    nextButtonText: '',
  });

  const onCloseHandler = React.useCallback(() => {
    setTooltipOpen(false);
    setBannerVisible(false);
    setStopOnbording(true);
    dispatch(markOnboardingAsViewed());
  }, [dispatch]);

  const onCloseBanner = React.useCallback(() => {
    trackOnbordingCloseClick('onboarding_banner');
    onCloseHandler();
  }, [onCloseHandler]);

  const onCloseButtonClick = React.useCallback(() => {
    const index = stopOnbording ? onbordingItems.length - 1 : currentIndex - 1;
    trackOnbordingCloseClick(onbordingItems[index].anchorBlock);
    onCloseHandler();
  }, [currentIndex, onCloseHandler, onbordingItems, stopOnbording]);

  const setNextIndex = React.useCallback(() => {
    const nextIndex = currentIndex + 1 < onbordingItems.length ? currentIndex + 1 : null;

    if (nextIndex) {
      setCurrentIndex(nextIndex);
    } else {
      setStopOnbording(true);
    }
  }, [currentIndex, onbordingItems.length]);

  const nextOnbordingHandler = React.useCallback(() => {
    if (stopOnbording) {
      onCloseHandler();
      scrollToBlock(EScrollingBlocks.DealTypeTabs);
      trackOnbordingItemClick(onbordingItems[onbordingItems.length - 1].anchorBlock);

      return;
    }

    setBannerVisible(false);

    const blockId = onbordingItems[currentIndex].anchorBlock;
    const block = document.getElementById(blockId);

    if (currentIndex !== 0) {
      trackOnbordingItemClick(onbordingItems[currentIndex - 1].anchorBlock);
    }

    setContent({
      title: onbordingItems[currentIndex].title,
      description: onbordingItems[currentIndex].description,
      nextButtonText: currentIndex === onbordingItems.length - 1 ? 'Отлично, буду знать' : 'Далее',
    });

    setNextIndex();

    if (block) {
      const top = block.getBoundingClientRect().top + window.scrollY;
      const left = block.getBoundingClientRect().left;

      setCoords({ top, left });

      window.scrollTo({
        top: top - ONBORDING_HEIGHT,
        behavior: 'smooth',
      });

      setTooltipOpen(true);

      window.addEventListener('resize', () => {
        const top = block.getBoundingClientRect().top + window.scrollY;
        const left = block.getBoundingClientRect().left;

        setCoords({ top, left });
      });
    } else {
      nextOnbordingHandler();
    }
  }, [currentIndex, onCloseHandler, onbordingItems, scrollToBlock, setNextIndex, stopOnbording]);

  const onStartOnbording = React.useCallback(() => {
    setBannerVisible(false);
    dispatch(markOnboardingAsViewed());
    trackOnbordingItemClick('onboarding_banner');

    // даем время скрыться баннеру
    setTimeout(nextOnbordingHandler);
  }, [dispatch, nextOnbordingHandler]);

  if (onbordingItems.length === 0) {
    return null;
  }

  return (
    <ActionAfterViewObserver callbackDelay={VIEWED_TIMEOUT} callback={trackViewOnbordingBanner}>
      <>
        {bannerVisible && (
          <OnbordingBanner isDesktop={isDesktop} onStartOnbording={onStartOnbording} onCloseOnbording={onCloseBanner} />
        )}
        {tooltipOpen && (
          <Portal>
            <OnbordingTooltip
              top={coords.top}
              left={coords.left}
              onClose={onCloseButtonClick}
              onClick={nextOnbordingHandler}
              title={content.title}
              description={content.description}
              nextButtonText={content.nextButtonText}
            />
          </Portal>
        )}
      </>
    </ActionAfterViewObserver>
  );
};

OnbordingContainer.displayName = 'OnbordingContainer';
